import { Controller } from "@hotwired/stimulus";

/**
 * Google Map Controller
 *
 * Handles Google Maps initialization with custom circular image markers.
 * Each marker links directly to a URL when clicked.
 */
export default class extends Controller {
  static targets = ["map"];
  static values = {
    markers: Array,
    center: Object,
    zoom: Number,
    apiKey: String,
  };

  connect() {
    if (!this.apiKeyValue) {
      console.warn("Google Maps API key not configured");
      return;
    }

    this.loadGoogleMapsApi().then(() => {
      this.initMap();
    });
  }

  disconnect() {
    if (this.map) {
      this.map = null;
    }
    if (this.overlays) {
      this.overlays.forEach((overlay) => overlay.setMap(null));
      this.overlays = [];
    }
  }

  /**
   * Load Google Maps API dynamically
   */
  async loadGoogleMapsApi() {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Check if script is already loading
      if (window.googleMapsLoading) {
        window.googleMapsCallbacks = window.googleMapsCallbacks || [];
        window.googleMapsCallbacks.push(resolve);
        return;
      }

      window.googleMapsLoading = true;
      window.googleMapsCallbacks = [resolve];

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&callback=initGoogleMapsCallback`;
      script.async = true;
      script.defer = true;

      window.initGoogleMapsCallback = () => {
        window.googleMapsLoading = false;
        window.googleMapsCallbacks.forEach((cb) => cb());
        window.googleMapsCallbacks = [];
        delete window.initGoogleMapsCallback;
      };

      script.onerror = () => {
        window.googleMapsLoading = false;
        reject(new Error("Failed to load Google Maps API"));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize the map
   */
  initMap() {
    if (!this.hasMapTarget) return;

    const mapOptions = {
      center: this.centerValue,
      zoom: this.zoomValue,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: this.getMapStyles(),
    };

    this.map = new google.maps.Map(this.mapTarget, mapOptions);
    this.overlays = [];
    this.createMarkers();
  }

  /**
   * Create custom markers
   */
  createMarkers() {
    this.markersValue.forEach((markerData) => {
      if (!markerData.imageUrl) return;
      const overlay = this.createCustomMarker(markerData);
      this.overlays.push(overlay);
    });
  }

  /**
   * Create a custom overlay marker with circular image
   */
  createCustomMarker(markerData) {
    const map = this.map;

    class CircularImageMarker extends google.maps.OverlayView {
      constructor(position, imageUrl, linkUrl) {
        super();
        this.position = position;
        this.imageUrl = imageUrl;
        this.linkUrl = linkUrl;
        this.div = null;
      }

      onAdd() {
        this.div = document.createElement("div");
        this.div.className = "google-map-marker";

        const link = document.createElement("a");
        link.href = this.linkUrl || "#";
        if (this.linkUrl) {
          link.target = "_self";
        }

        const img = document.createElement("img");
        img.src = this.imageUrl;
        img.alt = "";

        link.appendChild(img);
        this.div.appendChild(link);

        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(this.div);
      }

      draw() {
        const overlayProjection = this.getProjection();
        const pos = overlayProjection.fromLatLngToDivPixel(this.position);

        if (this.div) {
          // Center the marker (76px / 2 = 38px)
          this.div.style.position = "absolute";
          this.div.style.left = pos.x - 38 + "px";
          this.div.style.top = pos.y - 38 + "px";
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }
    }

    const position = new google.maps.LatLng(markerData.lat, markerData.lng);
    const overlay = new CircularImageMarker(
      position,
      markerData.imageUrl,
      markerData.linkUrl
    );
    overlay.setMap(map);

    return overlay;
  }

  /**
   * Optional: Custom map styles to match design
   */
  getMapStyles() {
    return [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ];
  }
}
